import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { format, addDays, isSameDay, parseISO } from 'date-fns';
import {
  Calendar as CalendarIcon, Clock, User, Video, Heart, Users,
  ChevronLeft, ChevronRight, Check, X, Loader2, Phone, Home
} from 'lucide-react';

const APPOINTMENT_TYPES = [
  { value: 'nurse_visit', label: 'Nurse Visit', icon: Heart, color: 'text-pink-500', duration: 60 },
  { value: 'social_worker', label: 'Social Worker', icon: Users, color: 'text-blue-500', duration: 45 },
  { value: 'chaplain', label: 'Chaplain Visit', icon: Heart, color: 'text-purple-500', duration: 30 },
  { value: 'telehealth', label: 'Telehealth Call', icon: Video, color: 'text-green-500', duration: 30 },
  { value: 'family_conference', label: 'Family Conference', icon: Users, color: 'text-orange-500', duration: 60 },
];

export function AppointmentSelfScheduler({ patientId, patientName }) {
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  // Generate mock available slots
  useEffect(() => {
    if (selectedDate && selectedType) {
      setLoading(true);
      
      // Simulate API call
      setTimeout(() => {
        const slots = [];
        const baseHour = 8;
        const typeInfo = APPOINTMENT_TYPES.find(t => t.value === selectedType);
        
        for (let hour = baseHour; hour < 17; hour++) {
          if (Math.random() > 0.3) { // 70% availability
            slots.push({
              id: `slot_${hour}_00`,
              start_time: new Date(selectedDate.setHours(hour, 0, 0)).toISOString(),
              end_time: new Date(selectedDate.setHours(hour, typeInfo?.duration || 60, 0)).toISOString(),
              staff_name: ['Sarah Johnson, RN', 'Michael Chen, SW', 'Emily Davis'][Math.floor(Math.random() * 3)],
              staff_role: typeInfo?.label || 'Staff',
            });
          }
          if (Math.random() > 0.5 && hour < 16) {
            slots.push({
              id: `slot_${hour}_30`,
              start_time: new Date(selectedDate.setHours(hour, 30, 0)).toISOString(),
              end_time: new Date(selectedDate.setHours(hour + 1, typeInfo?.duration || 60, 0)).toISOString(),
              staff_name: ['Sarah Johnson, RN', 'Michael Chen, SW', 'Emily Davis'][Math.floor(Math.random() * 3)],
              staff_role: typeInfo?.label || 'Staff',
            });
          }
        }
        
        setAvailableSlots(slots);
        setLoading(false);
      }, 500);
    }
  }, [selectedDate, selectedType]);

  // Load upcoming appointments
  useEffect(() => {
    setUpcomingAppointments([
      {
        id: 'apt_1',
        type: 'nurse_visit',
        date: addDays(new Date(), 2).toISOString(),
        time: '10:00 AM',
        staff: 'Sarah Johnson, RN',
        status: 'confirmed',
      },
      {
        id: 'apt_2',
        type: 'telehealth',
        date: addDays(new Date(), 5).toISOString(),
        time: '2:30 PM',
        staff: 'Dr. Williams',
        status: 'confirmed',
      },
    ]);
  }, []);

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setSelectedSlot(null);
    setStep(2);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setStep(3);
  };

  const handleConfirmBooking = async () => {
    setLoading(true);
    
    // Simulate booking API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setLoading(false);
    setBookingSuccess(true);
    setShowConfirmDialog(false);
    
    toast.success('Appointment requested successfully!');
    
    // Reset after delay
    setTimeout(() => {
      setStep(1);
      setSelectedType(null);
      setSelectedDate(null);
      setSelectedSlot(null);
      setNotes('');
      setBookingSuccess(false);
    }, 3000);
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">What type of appointment do you need?</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {APPOINTMENT_TYPES.map((type) => {
          const Icon = type.icon;
          return (
            <Card
              key={type.value}
              className={`cursor-pointer transition-all hover:shadow-md ${
                selectedType === type.value ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => handleTypeSelect(type.value)}
              data-testid={`appointment-type-${type.value}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full bg-slate-100 dark:bg-slate-800`}>
                    <Icon className={`w-5 h-5 ${type.color}`} />
                  </div>
                  <div>
                    <p className="font-medium">{type.label}</p>
                    <p className="text-sm text-muted-foreground">{type.duration} minutes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );

  const renderStep2 = () => {
    const typeInfo = APPOINTMENT_TYPES.find(t => t.value === selectedType);
    const Icon = typeInfo?.icon || CalendarIcon;
    
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
          <Icon className={`w-4 h-4 ${typeInfo?.color}`} />
          <span>{typeInfo?.label}</span>
          <span>•</span>
          <span>{typeInfo?.duration} minutes</span>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Calendar */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Select a Date</h3>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              disabled={(date) => date < new Date() || date > addDays(new Date(), 30)}
              className="rounded-md border"
            />
          </div>
          
          {/* Time Slots */}
          <div>
            <h3 className="text-lg font-semibold mb-3">
              {selectedDate ? `Available Times for ${format(selectedDate, 'MMMM d')}` : 'Select a date first'}
            </h3>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : selectedDate ? (
              <ScrollArea className="h-72">
                {availableSlots.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">
                    No available slots for this date. Please try another day.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {availableSlots.map((slot) => (
                      <Card
                        key={slot.id}
                        className={`cursor-pointer transition-all hover:shadow-sm ${
                          selectedSlot?.id === slot.id ? 'ring-2 ring-primary bg-primary/5' : ''
                        }`}
                        onClick={() => handleSlotSelect(slot)}
                        data-testid={`time-slot-${slot.id}`}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">
                                {format(parseISO(slot.start_time), 'h:mm a')}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">
                                {slot.staff_name}
                              </span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            ) : (
              <div className="flex items-center justify-center h-72 border rounded-md bg-muted/50">
                <p className="text-muted-foreground">Select a date to see available times</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    const typeInfo = APPOINTMENT_TYPES.find(t => t.value === selectedType);
    
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold">Confirm Your Appointment</h3>
        
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Appointment Type</span>
                <span className="font-medium">{typeInfo?.label}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Date</span>
                <span className="font-medium">{selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Time</span>
                <span className="font-medium">
                  {selectedSlot && format(parseISO(selectedSlot.start_time), 'h:mm a')}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Provider</span>
                <span className="font-medium">{selectedSlot?.staff_name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Duration</span>
                <span className="font-medium">{typeInfo?.duration} minutes</span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <div className="space-y-2">
          <Label htmlFor="notes">Additional Notes (optional)</Label>
          <Textarea
            id="notes"
            placeholder="Any special requests or information for this visit..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
        </div>
      </div>
    );
  };

  if (bookingSuccess) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Appointment Requested!</h2>
          <p className="text-muted-foreground mb-4">
            Your appointment request has been submitted. You will receive a confirmation once approved.
          </p>
          <Badge className="bg-green-100 text-green-800">Confirmation will be sent to your email</Badge>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-6" data-testid="appointment-scheduler">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">Schedule an Appointment</h1>
        <p className="text-muted-foreground">
          Book a visit or call with your care team at a time that works for you
        </p>
      </div>
      
      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4 mb-8">
        {[1, 2, 3].map((s) => (
          <React.Fragment key={s}>
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                step >= s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}
            >
              {s}
            </div>
            {s < 3 && (
              <div className={`w-16 h-1 ${step > s ? 'bg-primary' : 'bg-muted'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
      
      {/* Step Labels */}
      <div className="flex justify-center gap-16 text-sm text-muted-foreground mb-8">
        <span className={step === 1 ? 'text-primary font-medium' : ''}>Type</span>
        <span className={step === 2 ? 'text-primary font-medium' : ''}>Date & Time</span>
        <span className={step === 3 ? 'text-primary font-medium' : ''}>Confirm</span>
      </div>
      
      {/* Step Content */}
      <Card>
        <CardContent className="p-6">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </CardContent>
      </Card>
      
      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setStep(Math.max(1, step - 1))}
          disabled={step === 1}
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        {step < 3 ? (
          <Button
            onClick={() => setStep(step + 1)}
            disabled={(step === 1 && !selectedType) || (step === 2 && !selectedSlot)}
          >
            Continue
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={() => setShowConfirmDialog(true)}>
            <Check className="w-4 h-4 mr-2" />
            Request Appointment
          </Button>
        )}
      </div>
      
      {/* Upcoming Appointments */}
      {upcomingAppointments.length > 0 && (
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-lg">Your Upcoming Appointments</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingAppointments.map((apt) => {
                const typeInfo = APPOINTMENT_TYPES.find(t => t.value === apt.type);
                const Icon = typeInfo?.icon || CalendarIcon;
                
                return (
                  <div key={apt.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full bg-white dark:bg-slate-800`}>
                        <Icon className={`w-4 h-4 ${typeInfo?.color}`} />
                      </div>
                      <div>
                        <p className="font-medium">{typeInfo?.label}</p>
                        <p className="text-sm text-muted-foreground">
                          {format(parseISO(apt.date), 'EEEE, MMMM d')} at {apt.time}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={apt.status === 'confirmed' ? 'default' : 'secondary'}>
                        {apt.status}
                      </Badge>
                      <Button variant="ghost" size="sm">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Appointment Request</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Are you sure you want to request this appointment? The care team will review and confirm shortly.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmBooking} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Requesting...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Confirm
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AppointmentSelfScheduler;
